/**
 * Audio Gate
 * ----------
 * Asks: "Enable Audio?" before narration starts.
 * Blocks background interaction.
 * HUD & observers listen for `sc:audio-pref-changed`.
 * Handles scroll lock, modal focus, and narration intro.
 */



let prevOverflow = "";

/**
 * Create a temporary blur/dim overlay on the first section while the gate is visible.
 * Returns a cleanup function that fades it out and removes it.
 */
function mountGateBackdrop(): () => void {
  const section = document.getElementById("section-0") as HTMLElement | null;
  if (!section) {
    // Fallback: try first section by data attribute
    const first = document.querySelector("[data-section-index='0']") as HTMLElement | null;
    if (!first) return () => {};
    return createOn(first);
  }
  return createOn(section);

  function createOn(host: HTMLElement): () => void {
    host.classList.add("is-primed");
    const overlay = document.createElement("div");
    overlay.className = "gate-overlay";
    Object.assign(overlay.style, {
      position: "absolute",
      inset: "0",
      pointerEvents: "none",
      zIndex: "700",
      opacity: "0",
      transition: "opacity 200ms ease",
      background: "rgba(0,0,0,0.25)",
      // backdrop-filter is not typed in CSSStyleDeclaration; cast to any
    } as Partial<CSSStyleDeclaration>);
    (overlay.style as any).backdropFilter = "blur(6px)";

    // Ensure host can position absolute children
    const prevPos = host.style.position || "";
    if (!prevPos) host.style.position = "relative";

    host.appendChild(overlay);
    requestAnimationFrame(() => {
      overlay.style.opacity = "1";
    });

    // Cleanup: fade then remove
    return () => {
      try { host.classList.remove("is-primed"); } catch {}
      try {
        overlay.style.opacity = "0";
        const removal = () => {
          try { overlay.remove(); } catch {}
          // Restore previous positioning if we changed it
          if (!prevPos) host.style.position = "";
        };
        overlay.addEventListener("transitionend", removal, { once: true });
        setTimeout(removal, 300); // fallback
      } catch {
        try { overlay.remove(); } catch {}
        if (!prevPos) host.style.position = "";
      }
    };
  }
}

function setBackgroundInert(on: boolean) {
  const root = document.getElementById("scroll-wrapper") || document.body;
  if (on) root.setAttribute("inert", "");
  else root.removeAttribute("inert");
}

function lockScroll() {
  prevOverflow = document.body.style.overflow;
  document.body.style.overflow = "hidden";
  document.documentElement.classList.add("gate-hold");
  document.body.classList.add("gate-hold");
}

function unlockScroll() {
  document.body.style.overflow = prevOverflow;
  document.documentElement.classList.remove("gate-hold");
  document.body.classList.remove("gate-hold");
}

function dismissGate(container: HTMLElement, afterClose?: () => void, beforeRemove?: () => void) {
  container.style.opacity = "0";
  container.classList.remove("fade-in");
  container.classList.add("fade-out");
  // 🚀 Ensure it stops blocking arrows immediately
  container.style.pointerEvents = "none";

  let cleanedUp = false;
  const cleanup = () => {
    if (cleanedUp) return;
    cleanedUp = true;
    try { beforeRemove?.(); } catch {}
    setBackgroundInert(false);
    container.remove();
    unlockScroll();
    try { afterClose?.(); } catch {}
  };

  // catch either CSS animation or transition end
  const once = { once: true } as const;
  container.addEventListener("animationend", cleanup, once);
  container.addEventListener("transitionend", cleanup, once);

  // Fallback in case neither event fires
  setTimeout(cleanup, 1000);
}

function trapFocus(element: HTMLElement) {
  const focusableSelectors =
    'a[href], area[href], input:not([disabled]):not([type="hidden"]), select:not([disabled]), textarea:not([disabled]), button:not([disabled]), iframe, object, embed, [tabindex="0"], [contenteditable]';
  const focusableElements = Array.from(element.querySelectorAll(focusableSelectors)) as HTMLElement[];
  if (focusableElements.length === 0) return () => {};

  const firstElement = focusableElements[0];
  const lastElement = focusableElements[focusableElements.length - 1];

  const handleFocus = (event: KeyboardEvent) => {
    if (event.key !== "Tab") return;

    if (event.shiftKey) {
      if (document.activeElement === firstElement) {
        event.preventDefault();
        lastElement.focus();
      }
    } else {
      if (document.activeElement === lastElement) {
        event.preventDefault();
        firstElement.focus();
      }
    }
  };

  element.addEventListener("keydown", handleFocus);
  // Ensure container itself can receive key events even if nothing focused yet
  (element as HTMLElement).tabIndex = -1;
  firstElement.focus();

  return () => element.removeEventListener("keydown", handleFocus);
}

export function showAudioGate(
  attrs: Record<string, any>,
  _content: string,
  onComplete: (optedIn: boolean) => void
): HTMLElement {
  const existing = document.getElementById("audio-gate-container");
  if (existing) {
    onComplete(localStorage.getItem("storycore-audio-muted") === "0");
    return existing as HTMLElement;
  }

  // --- URL param override: skip audio gate entirely for audits ---
  try {
    const params = new URLSearchParams(window.location.search);
    const skipGate = params.has("nogate") || params.has("audit");
    if (skipGate) {
      console.debug("[AUDIO-GATE] Skipping gate via URL param (?nogate|?audit)");
      // Do not touch global audio state here; let the caller handle it.
      onComplete(false);
      return document.createElement("div");
    }
  } catch {}

  // Skip reels — HUD handles tap-to-play
  if (attrs["video-reel"]) {
    onComplete(false);
    return document.createElement("div");
  }

  lockScroll();
  setBackgroundInert(true);

  // While the gate is up, lightly blur/dim the first section
  const cleanupBackdrop = mountGateBackdrop();

  const container = document.createElement("div");

  const prefersReduced = window.matchMedia && window.matchMedia("(prefers-reduced-motion: reduce)").matches;

  // Ensure it fades even if theme CSS is missing
  Object.assign(container.style, {
    position: "fixed",
    inset: "0",
    zIndex: String(2147483647),
    display: "grid",
    placeItems: "center",
    pointerEvents: "auto",
    opacity: "0",
    transition: `opacity ${prefersReduced ? "0.01s" : "0.4s"} ease`,
  });

  container.id = "audio-gate-container";
  container.classList.add("audio-gate", "audio-gate-overlay", "fade-in");
  container.setAttribute("role", "dialog");
  container.setAttribute("aria-modal", "true");
  container.setAttribute("aria-labelledby", "audio-gate-title");
  container.setAttribute("aria-describedby", "audio-gate-desc");

  container.innerHTML = `
    <div class="audio-prompt">
      <h2 id="audio-gate-title">
        Enable Audio?
      </h2>
      <p id="audio-gate-desc">
        This story includes narration for a richer experience.
      </p>
      <div class="audio-prompt-buttons">
        <button id="audio-enable" class="btn-enable">Enable</button>
        <button id="audio-decline" class="btn-decline">No Thanks</button>
      </div>
      <button id="audio-blog" class="btn-read">Prefer to read instead?</button>
    </div>
  `;

  // Accessibility labels for buttons
  (container.querySelector("#audio-enable") as HTMLButtonElement | null)?.setAttribute("aria-label", "Enable narration");
  (container.querySelector("#audio-decline") as HTMLButtonElement | null)?.setAttribute("aria-label", "Decline narration");

  // Guard: ensure the modal can receive focus even if no focusables rendered
  if (!container.querySelector("button, [href], input, select, textarea, [tabindex]")) {
    (container as HTMLElement).tabIndex = -1;
    container.focus();
  }

  // Accessibility: trap focus within the modal
  const cleanupTrap = trapFocus(container);

  // Close gate on Escape key (document-level, capture=true) so it works even if focus wanders
  const onEsc = (e: KeyboardEvent) => {
    if (e.key === "Escape") {
      e.preventDefault();
      (container.querySelector("#audio-decline") as HTMLElement | null)?.click();
    }
  };
  document.addEventListener("keydown", onEsc, { capture: true });

  // Compose beforeRemove cleanup to remove focus trap and ESC listener
  const beforeRemove = () => {
    try { cleanupTrap(); } catch {}
    try { document.removeEventListener("keydown", onEsc, { capture: true } as any); } catch {}
    try { cleanupBackdrop(); } catch {}
  };

  // Button handlers (with simple debounce)
  let resolving = false;
  const guard = () => { if (resolving) return true; resolving = true; return false; };

  // Button handlers
  container.querySelector("#audio-enable")?.addEventListener("click", () => {
    if (guard()) return;
    dismissGate(container, () => onComplete(true), beforeRemove);
    console.debug("[AUDIO-GATE] user enabled audio");
  });

  container.querySelector("#audio-decline")?.addEventListener("click", () => {
    if (guard()) return;
    dismissGate(container, () => onComplete(false), beforeRemove);
    console.debug("[AUDIO-GATE] user declined audio");
  });

  const resolveBlogUrl = (): string => {
    try {
      const meta = document.querySelector('meta[name="storycore-blog-url"]') as HTMLMetaElement | null;
      const url = meta?.content?.trim();
      if (url) return url;
    } catch {}
    return `${window.location.pathname}?mode=read`;
  };

  container.querySelector("#audio-blog")?.addEventListener("click", () => {
    dismissGate(container, undefined, beforeRemove);
    onComplete(false);
    window.location.href = resolveBlogUrl();
    console.debug("[AUDIO-GATE] user switched to read mode");
  });

  document.body.appendChild(container);

  // Kick the fade-in: start at 0 (with .fade-in + inline opacity), then lift to 1 next frame
  requestAnimationFrame(() => {
    container.classList.remove("fade-in");
    container.style.opacity = "1";
  });

  console.debug(
    "[AUDIO-GATE] mounted",
    {
      id: container.id,
      classes: Array.from(container.classList),
      hasAudioPrompt: container.querySelector(".audio-prompt") !== null,
      opacity: window.getComputedStyle(container).opacity,
    }
  );

  return container;
}
